// Sandbox mode for the QC Tool → Issues Resolution two-tool workflow.
//
// Turn on by setting VITE_SANDBOX_MODE=true in .env.local. While on:
//   - QC Tool injects a "[TEST] Sandbox Market" shopper into its dataset so the
//     full QC flow is exercisable without uploading a real CSV.
//   - All DB-write calls in src/services/dataService.js are short-circuited.
//     The payload that would have been POSTed is pretty-printed to the console
//     and a success-shaped result is returned so the UI proceeds normally.
//
// Read-only fetches (GETs) are NOT intercepted — they still hit prod — with
// one exception: GET /spots is served from seed data, because the spot ids the
// sandbox issue rows carry are invented by the sandbox itself. See
// getSandboxSpotProducts below. Keep sandbox to UAT / local; do not enable in
// a build aimed at end users.

export const SANDBOX_MODE = import.meta.env.VITE_SANDBOX_MODE === 'true';

// Two sandbox shoppers:
//   1. [TEST] Sandbox Market — small, pre-seeded with QC issues so you can land
//      directly on the IR dashboard and reproduce the prod bugs.
//   2. Supermax — larger and "fresh" (no seeded swissues rows). Used to walk
//      the FULL operator workflow: open in QC Tool, mark issues, Push to
//      ledger, then watch them appear on the IR dashboard.
export const SANDBOX_RETAILER = '[TEST] Sandbox Market';
export const SUPERMAX_RETAILER = 'Supermax';
export const SANDBOX_RETAILERS = [SANDBOX_RETAILER, SUPERMAX_RETAILER];

// Retailer-name prefix applied to CSVs uploaded via "Add Sandbox Shopper" so
// the row carries its sandbox provenance through every downstream surface
// (shopper card, ledger row, IR dashboard, exports) without needing a separate
// flag column. Keep this in sync with isSandboxRetailer below.
export const SANDBOX_UPLOAD_PREFIX = '[SANDBOX] ';

// Truthy for any retailer the system should treat as sandbox-origin: the two
// seeded shoppers above, or any upload that went through "Add Sandbox Shopper".
export function isSandboxRetailer(name) {
  if (!name) return false;
  if (SANDBOX_RETAILERS.includes(name)) return true;
  return name.startsWith(SANDBOX_UPLOAD_PREFIX) || name.startsWith('[TEST]');
}

// Tiny inline SVG so the QC viewer has *something* to render for the test
// shoppers. Distinct labels per page/spot so it's obvious you're in sandbox.
const svgImage = (label, color = '#0ea5e9') =>
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 800">` +
      `<rect width="600" height="800" fill="${color}" opacity="0.08"/>` +
      `<rect x="20" y="20" width="560" height="760" fill="none" stroke="${color}" stroke-width="4" stroke-dasharray="12 8"/>` +
      `<text x="300" y="380" text-anchor="middle" font-family="sans-serif" font-size="48" fill="${color}" font-weight="700">SANDBOX</text>` +
      `<text x="300" y="440" text-anchor="middle" font-family="sans-serif" font-size="28" fill="${color}">${label}</text>` +
    `</svg>`
  );

// ---- [TEST] Sandbox Market images ----
const PAGE_1_IMG = svgImage('Page 1');
const PAGE_2_IMG = svgImage('Page 2');
const SPOT_A_IMG = svgImage('Spot A', '#0284c7');
const SPOT_B_IMG = svgImage('Spot B', '#0284c7');
const SPOT_C_IMG = svgImage('Spot C', '#0284c7');

// ---- Supermax images (different color so they're visually distinguishable) ----
const SMX_P1_IMG = svgImage('Supermax · P1', '#15803d');
const SMX_P2_IMG = svgImage('Supermax · P2', '#15803d');
const SMX_S1_IMG = svgImage('Beverages', '#16a34a');
const SMX_S2_IMG = svgImage('Snacks', '#16a34a');
const SMX_S3_IMG = svgImage('Cereal', '#16a34a');
const SMX_S4_IMG = svgImage('Dairy', '#16a34a');
const SMX_S5_IMG = svgImage('Bread', '#16a34a');

// Fixed dates so the test shoppers are stable across reloads.
// Format matches the rest of swissues data: M/D/YYYY (see utils/dateUtils.js
// — parseDate splits on "/" and returns null for any other format, which would
// drop sandbox rows out of the "Last 30 Days" / "Last 3 Months" filters).
const START_DATE = '6/1/2026';
const END_DATE = '6/7/2026';

const SUPERMAX_START_DATE = '6/15/2026';
const SUPERMAX_END_DATE = '6/21/2026';

// CSV-shaped rows (camelCase keys, matching the parser in QCTool.jsx).
const TEST_MARKET_ROWS = [
  // ---------- Page 1, Spot A ----------
  { retailer: SANDBOX_RETAILER, startDate: START_DATE, endDate: END_DATE, pageNumber: 1, shopperId: 'SANDBOX-001', shopperImage: PAGE_1_IMG, spotId: 'SANDBOX-SPOT-A', spotImage: SPOT_A_IMG, upc: '000000000001', brand: 'Test Brand',    category: 'Beverages', description: 'Test Cola 12oz Can',         priceReg: '1.99', qtyReg: '1', priceSpe: '',     qtySpe: '' },
  { retailer: SANDBOX_RETAILER, startDate: START_DATE, endDate: END_DATE, pageNumber: 1, shopperId: 'SANDBOX-001', shopperImage: PAGE_1_IMG, spotId: 'SANDBOX-SPOT-A', spotImage: SPOT_A_IMG, upc: '000000000002', brand: 'Test Brand',    category: 'Beverages', description: 'Test Diet Cola 12oz Can',    priceReg: '1.99', qtyReg: '1', priceSpe: '',     qtySpe: '' },
  // ---------- Page 1, Spot B ----------
  { retailer: SANDBOX_RETAILER, startDate: START_DATE, endDate: END_DATE, pageNumber: 1, shopperId: 'SANDBOX-001', shopperImage: PAGE_1_IMG, spotId: 'SANDBOX-SPOT-B', spotImage: SPOT_B_IMG, upc: '000000000003', brand: 'Sandbox Snacks', category: 'Snacks',    description: 'Test Potato Chips Family Size', priceReg: '4.99', qtyReg: '1', priceSpe: '3.49', qtySpe: '2' },
  { retailer: SANDBOX_RETAILER, startDate: START_DATE, endDate: END_DATE, pageNumber: 1, shopperId: 'SANDBOX-001', shopperImage: PAGE_1_IMG, spotId: 'SANDBOX-SPOT-B', spotImage: SPOT_B_IMG, upc: '000000000004', brand: 'Sandbox Snacks', category: 'Snacks',    description: 'Test Pretzels 8oz',          priceReg: '3.99', qtyReg: '1', priceSpe: '',     qtySpe: '' },
  // ---------- Page 2, Spot C ----------
  { retailer: SANDBOX_RETAILER, startDate: START_DATE, endDate: END_DATE, pageNumber: 2, shopperId: 'SANDBOX-001', shopperImage: PAGE_2_IMG, spotId: 'SANDBOX-SPOT-C', spotImage: SPOT_C_IMG, upc: '000000000005', brand: 'Test Dairy',     category: 'Dairy',     description: 'Test Whole Milk Gallon',     priceReg: '5.49', qtyReg: '1', priceSpe: '',     qtySpe: '' },
];

// Supermax — bigger, realistic categories, no seeded swissues. Walk the whole
// QC flow against this one to simulate the full operator → reviewer process.
const SUPERMAX_ROWS = [
  // -------------------- Page 1 --------------------
  // Spot 1: Beverages
  { retailer: SUPERMAX_RETAILER, startDate: SUPERMAX_START_DATE, endDate: SUPERMAX_END_DATE, pageNumber: 1, shopperId: 'SMX-1001', shopperImage: SMX_P1_IMG, spotId: 'SMX-SPOT-1', spotImage: SMX_S1_IMG, upc: '049000050103', brand: 'Coca-Cola',   category: 'Beverages', description: 'Coca-Cola 2L Bottle',           priceReg: '2.99', qtyReg: '1', priceSpe: '1.99', qtySpe: '2' },
  { retailer: SUPERMAX_RETAILER, startDate: SUPERMAX_START_DATE, endDate: SUPERMAX_END_DATE, pageNumber: 1, shopperId: 'SMX-1001', shopperImage: SMX_P1_IMG, spotId: 'SMX-SPOT-1', spotImage: SMX_S1_IMG, upc: '012000001246', brand: 'Pepsi',       category: 'Beverages', description: 'Pepsi 2L Bottle',               priceReg: '2.99', qtyReg: '1', priceSpe: '1.99', qtySpe: '2' },
  // Spot 2: Snacks
  { retailer: SUPERMAX_RETAILER, startDate: SUPERMAX_START_DATE, endDate: SUPERMAX_END_DATE, pageNumber: 1, shopperId: 'SMX-1001', shopperImage: SMX_P1_IMG, spotId: 'SMX-SPOT-2', spotImage: SMX_S2_IMG, upc: '028400090827', brand: 'Doritos',     category: 'Snacks',    description: 'Doritos Nacho Cheese Family',   priceReg: '5.49', qtyReg: '1', priceSpe: '3.99', qtySpe: '1' },
  { retailer: SUPERMAX_RETAILER, startDate: SUPERMAX_START_DATE, endDate: SUPERMAX_END_DATE, pageNumber: 1, shopperId: 'SMX-1001', shopperImage: SMX_P1_IMG, spotId: 'SMX-SPOT-2', spotImage: SMX_S2_IMG, upc: '028400040150', brand: 'Lay\'s',     category: 'Snacks',    description: 'Lay\'s Classic Potato Chips',  priceReg: '4.99', qtyReg: '1', priceSpe: '',     qtySpe: '' },
  { retailer: SUPERMAX_RETAILER, startDate: SUPERMAX_START_DATE, endDate: SUPERMAX_END_DATE, pageNumber: 1, shopperId: 'SMX-1001', shopperImage: SMX_P1_IMG, spotId: 'SMX-SPOT-2', spotImage: SMX_S2_IMG, upc: '028400103961', brand: 'Cheetos',     category: 'Snacks',    description: 'Cheetos Crunchy Family Size',   priceReg: '4.99', qtyReg: '1', priceSpe: '',     qtySpe: '' },
  // Spot 3: Cereal
  { retailer: SUPERMAX_RETAILER, startDate: SUPERMAX_START_DATE, endDate: SUPERMAX_END_DATE, pageNumber: 1, shopperId: 'SMX-1001', shopperImage: SMX_P1_IMG, spotId: 'SMX-SPOT-3', spotImage: SMX_S3_IMG, upc: '016000275249', brand: 'General Mills', category: 'Cereal',  description: 'Cheerios 18oz Box',             priceReg: '4.49', qtyReg: '1', priceSpe: '2.99', qtySpe: '2' },
  { retailer: SUPERMAX_RETAILER, startDate: SUPERMAX_START_DATE, endDate: SUPERMAX_END_DATE, pageNumber: 1, shopperId: 'SMX-1001', shopperImage: SMX_P1_IMG, spotId: 'SMX-SPOT-3', spotImage: SMX_S3_IMG, upc: '038000596964', brand: 'Kellogg\'s',  category: 'Cereal',    description: 'Frosted Flakes 15oz Box',       priceReg: '4.49', qtyReg: '1', priceSpe: '2.99', qtySpe: '2' },

  // -------------------- Page 2 --------------------
  // Spot 4: Dairy
  { retailer: SUPERMAX_RETAILER, startDate: SUPERMAX_START_DATE, endDate: SUPERMAX_END_DATE, pageNumber: 2, shopperId: 'SMX-1001', shopperImage: SMX_P2_IMG, spotId: 'SMX-SPOT-4', spotImage: SMX_S4_IMG, upc: '041700102030', brand: 'Dairy Pure',  category: 'Dairy',     description: 'Whole Milk Gallon',             priceReg: '5.49', qtyReg: '1', priceSpe: '',     qtySpe: '' },
  { retailer: SUPERMAX_RETAILER, startDate: SUPERMAX_START_DATE, endDate: SUPERMAX_END_DATE, pageNumber: 2, shopperId: 'SMX-1001', shopperImage: SMX_P2_IMG, spotId: 'SMX-SPOT-4', spotImage: SMX_S4_IMG, upc: '052100078809', brand: 'Yoplait',     category: 'Dairy',     description: 'Yoplait Strawberry 6-pack',     priceReg: '4.99', qtyReg: '1', priceSpe: '3.99', qtySpe: '1' },
  { retailer: SUPERMAX_RETAILER, startDate: SUPERMAX_START_DATE, endDate: SUPERMAX_END_DATE, pageNumber: 2, shopperId: 'SMX-1001', shopperImage: SMX_P2_IMG, spotId: 'SMX-SPOT-4', spotImage: SMX_S4_IMG, upc: '021000615919', brand: 'Kraft',       category: 'Dairy',     description: 'Kraft American Cheese Slices',  priceReg: '4.49', qtyReg: '1', priceSpe: '',     qtySpe: '' },
  // Spot 5: Bread
  { retailer: SUPERMAX_RETAILER, startDate: SUPERMAX_START_DATE, endDate: SUPERMAX_END_DATE, pageNumber: 2, shopperId: 'SMX-1001', shopperImage: SMX_P2_IMG, spotId: 'SMX-SPOT-5', spotImage: SMX_S5_IMG, upc: '073410015421', brand: 'Wonder',      category: 'Bread',     description: 'Wonder Wheat Bread Loaf',       priceReg: '3.99', qtyReg: '1', priceSpe: '',     qtySpe: '' },
  { retailer: SUPERMAX_RETAILER, startDate: SUPERMAX_START_DATE, endDate: SUPERMAX_END_DATE, pageNumber: 2, shopperId: 'SMX-1001', shopperImage: SMX_P2_IMG, spotId: 'SMX-SPOT-5', spotImage: SMX_S5_IMG, upc: '073410017654', brand: 'Wonder',      category: 'Bread',     description: 'Wonder White Bread Loaf',       priceReg: '3.49', qtyReg: '1', priceSpe: '',     qtySpe: '' },
];

export const SANDBOX_ROWS = [...TEST_MARKET_ROWS, ...SUPERMAX_ROWS];

// --- Intercepted-POST log ---------------------------------------------------
//
// In-memory ring buffer plus a tiny pub-sub so the SandboxConsole component
// can render every intercepted POST in real time without having to open
// DevTools. logSandboxPost both prints to the console (handy for power users)
// and pushes onto the buffer (drives the in-app panel).

const MAX_ENTRIES = 100;
let entries = [];
let nextId = 1;
const subscribers = new Set();

function notify() {
  for (const fn of subscribers) {
    try { fn(entries); } catch (e) { console.error('[sandbox] subscriber error:', e); }
  }
}

export function getSandboxEntries() {
  return entries;
}

export function subscribeSandbox(fn) {
  subscribers.add(fn);
  fn(entries);
  return () => subscribers.delete(fn);
}

export function clearSandboxEntries() {
  entries = [];
  notify();
}

// Pretty-print a payload that would have been POSTed to the backend.
// Groups output under a console.group so it's scannable in DevTools, AND
// pushes onto the in-memory buffer so the in-app SandboxConsole can show it.
export function logSandboxPost(label, payload) {
  if (typeof console.groupCollapsed === 'function') {
    console.groupCollapsed(`%c[SANDBOX] ${label}`, 'color:#0ea5e9;font-weight:bold');
    console.log(payload);
    console.groupEnd();
  } else {
    console.log(`[SANDBOX] ${label}`, payload);
  }

  const entry = {
    id: nextId++,
    timestamp: new Date(),
    label,
    payload,
  };
  entries = [entry, ...entries].slice(0, MAX_ENTRIES);
  notify();
}

// --- In-memory swissues store ----------------------------------------------
//
// Seeded with rows that mirror the prod bug shape: a mix of Fail and Pass on
// the [TEST] Sandbox Market shopper. With "Show All Entries" off, only the
// Fail rows surface in the IR card list — same as prod. With it on, the Pass
// rows reveal themselves and you can see the Resolved/Escalated buttons render
// on rows that should never have been markable.
//
// All seed IDs are prefixed `sandbox-` so they're obvious in the PATCH payload.
// State changes via updateSandboxIssue persist for the browser session;
// a hard refresh reseeds.

const SANDBOX_USER_EMAIL = 'sandbox@test.local';

function nowIso() {
  return new Date().toISOString();
}

// Seed mirrors a realistic QC ledger push using the canonical QC Tool issue
// taxonomy (see QCTool.jsx selects):
//   Page-level    : Missing Spot | Wrong Date(s) | Wrong Retailer
//   Spot-level    : Missing Product | Delete Spot
//   Product/UPC   : Incorrect Product | Wrong Price
// Every level emits both Fail and Pass(/Context) rows in real pushes, so the
// seed includes Pass rows too — but their qc_status is now correctly 'Pass'
// (not null, which was the prior bug).
function buildSeedIssues() {
  const created = nowIso();
  const base = {
    source: 'qc_tool',
    retailer: SANDBOX_RETAILER,
    date: START_DATE,
    shopper_id: 'SANDBOX-001',
    user_id: 'sandbox-user',
    user_email: SANDBOX_USER_EMAIL,
    resolution_status: null,
    resolution_notes: null,
    suggested_upc_spot: null,
    observation: null,
    meta: null,
    created_at: created,
    updated_at: created,
  };
  return [
    // -------------------------- Page 1 (no page error) ----------------------
    { ...base, id: 'sandbox-issue-p1-page',  page_number: 1, level: 'Page',                  spot_id: null,             upc: null,           description: null,                        issue_type: null,             qc_status: 'Pass', idempotency_key: 'sandbox-seed-p1-page' },

    // Spot A — Fail: spot has Missing Product, and Product 1 has Wrong Price
    { ...base, id: 'sandbox-issue-p1-sa-spot', page_number: 1, level: 'Spot',                  spot_id: 'SANDBOX-SPOT-A', upc: null,           description: null,                        issue_type: 'Missing Product', qc_status: 'Fail', suggested_upc_spot: '000000000099', observation: 'Expected promo-pack 6-count missing from spot.', idempotency_key: 'sandbox-seed-p1-sa-spot' },
    { ...base, id: 'sandbox-issue-p1-sa-p1',   page_number: 1, level: 'Product/UPC',           spot_id: 'SANDBOX-SPOT-A', upc: '000000000001', description: 'Test Cola 12oz Can',        issue_type: 'Wrong Price',     qc_status: 'Fail', observation: 'Printed $1.99 but shelf says $2.49.', idempotency_key: 'sandbox-seed-p1-sa-p1' },
    { ...base, id: 'sandbox-issue-p1-sa-p2',   page_number: 1, level: 'Product/UPC (Context)', spot_id: 'SANDBOX-SPOT-A', upc: '000000000002', description: 'Test Diet Cola 12oz Can',   issue_type: null,              qc_status: 'Pass', idempotency_key: 'sandbox-seed-p1-sa-p2' },

    // Spot B — Spot is fine, but Product 3 is the wrong item (Incorrect Product)
    { ...base, id: 'sandbox-issue-p1-sb-spot', page_number: 1, level: 'Spot',                  spot_id: 'SANDBOX-SPOT-B', upc: null,           description: null,                        issue_type: null,              qc_status: 'Pass', idempotency_key: 'sandbox-seed-p1-sb-spot' },
    { ...base, id: 'sandbox-issue-p1-sb-p3',   page_number: 1, level: 'Product/UPC',           spot_id: 'SANDBOX-SPOT-B', upc: '000000000003', description: 'Test Potato Chips Family',  issue_type: 'Incorrect Product', qc_status: 'Fail', suggested_upc_spot: '000000000098', observation: 'Catalog points to chips, image shows pretzels.', idempotency_key: 'sandbox-seed-p1-sb-p3' },
    { ...base, id: 'sandbox-issue-p1-sb-p4',   page_number: 1, level: 'Product/UPC (Context)', spot_id: 'SANDBOX-SPOT-B', upc: '000000000004', description: 'Test Pretzels 8oz',         issue_type: null,              qc_status: 'Pass', idempotency_key: 'sandbox-seed-p1-sb-p4' },

    // -------------------------- Page 2 (page-level error) -------------------
    { ...base, id: 'sandbox-issue-p2-page',  page_number: 2, level: 'Page',                  spot_id: null,             upc: null,           description: null,                        issue_type: 'Missing Spot',    qc_status: 'Fail', observation: 'Expected dairy spot is absent from this page.', idempotency_key: 'sandbox-seed-p2-page' },

    // Spot C — clean
    { ...base, id: 'sandbox-issue-p2-sc-spot', page_number: 2, level: 'Spot',                  spot_id: 'SANDBOX-SPOT-C', upc: null,           description: null,                        issue_type: null,              qc_status: 'Pass', idempotency_key: 'sandbox-seed-p2-sc-spot' },
    { ...base, id: 'sandbox-issue-p2-sc-p5',   page_number: 2, level: 'Product/UPC (Context)', spot_id: 'SANDBOX-SPOT-C', upc: '000000000005', description: 'Test Whole Milk Gallon',    issue_type: null,              qc_status: 'Pass', idempotency_key: 'sandbox-seed-p2-sc-p5' },
  ];
}

// Seed rows for the New Products Requests page (source: pm_new_product).
// Shape mirrors the payload ProductsManager posts (ProductsManager.jsx:1413) —
// variety type rides in `level`, and resolution_status is null until someone
// triages the request, which the UI renders as the "-" / New Requests bucket.
//
// Deliberately spread across all four status buckets, both variety types, and
// three requesters so the status pills, user dropdown, and variety-type
// dropdown all have something to filter. Fixed timestamps keep row order
// stable across reloads.
function buildSeedNewProductRequests() {
  const spotLink = (id) => `https://www.abconsultingpr.com/sw1dbtables/spots/${id}/`;
  const base = {
    source: 'pm_new_product',
    retailer: SANDBOX_RETAILER,
    date: START_DATE,
    shopper_id: null,
    page_number: null,
    spot_id: null,
    upc: null,
    description: null,
    issue_type: null,
    qc_status: null,
    observation: null,
    suggested_upc_spot: null,
    meta: null,
    user_id: 'sandbox-user',
  };

  const rows = [
    // ---- New Requests (resolution_status null → renders as "-") ----
    ['npr-01', '482913', 'Goya Coconut Water 33.8oz Tetra Pak',                'New Variety',             'goya.com/products/coconut-water',        'maria.rivera@abconsultingpr.com', null,       null,                                                        '2026-08-20T14:05:00.000Z'],
    ['npr-02', '482914', 'Bounty Select-A-Size Paper Towels 8 Double Rolls',   'Extend Existing Variety', 'pg.com/bounty/select-a-size',            'maria.rivera@abconsultingpr.com', null,       null,                                                        '2026-08-20T13:40:00.000Z'],
    ['npr-03', '482915', 'Café Bustelo Espresso Ground Coffee 10oz Brick',     'New Variety',             '',                                       'luis.ortiz@abconsultingpr.com',   null,       null,                                                        '2026-08-19T17:22:00.000Z'],
    ['npr-04', '482916', 'Chobani Zero Sugar Vanilla Greek Yogurt 5.3oz Cup',  'New Variety',             'chobani.com/products/zero-sugar',        'ana.delgado@abconsultingpr.com',  null,       null,                                                        '2026-08-19T09:10:00.000Z'],

    // ---- Pending ----
    ['npr-05', '482917', 'Tide Pods Spring Meadow 42 Count Tub',               'Extend Existing Variety', 'tide.com/pods/spring-meadow',            'luis.ortiz@abconsultingpr.com',   'Pending',  'Waiting on UPC confirmation from the vendor.',              '2026-08-18T16:48:00.000Z'],
    ['npr-06', '482918', 'Malta India 12oz Glass Bottle 6-Pack',               'New Variety',             '',                                       'ana.delgado@abconsultingpr.com',  'Pending',  'Need the 6-pack image, only the single is on file.',        '2026-08-18T11:30:00.000Z'],
    ['npr-07', '482919', 'Nestlé La Lechera Sweetened Condensed Milk 14oz',    'Extend Existing Variety', 'nestle.com/lalechera',                   'maria.rivera@abconsultingpr.com', 'Pending',  '',                                                          '2026-08-17T15:02:00.000Z'],

    // ---- Reviewed ----
    ['npr-08', '482920', 'Kellogg\'s Special K Red Berries Cereal 16.9oz Box', 'New Variety',             'kelloggs.com/special-k/red-berries',     'ana.delgado@abconsultingpr.com',  'Reviewed', 'Matches an existing catalog entry — extending instead.',    '2026-08-17T10:15:00.000Z'],
    ['npr-09', '482921', 'Suavitel Fabric Softener Field Flowers 135oz',       'Extend Existing Variety', '',                                       'luis.ortiz@abconsultingpr.com',   'Reviewed', 'Approved, queued for the next catalog import.',             '2026-08-16T18:55:00.000Z'],
    ['npr-10', '482922', 'Bimbo Pan Sobao Sliced Bread 22oz Bag',              'New Variety',             'grupobimbo.com/pan-sobao',               'maria.rivera@abconsultingpr.com', 'Reviewed', '',                                                          '2026-08-16T08:20:00.000Z'],

    // ---- Created ----
    ['npr-11', '482923', 'Yaucono Ground Coffee 14oz Vacuum Pack',             'New Variety',             'yaucono.com/ground-coffee',              'ana.delgado@abconsultingpr.com',  'Created',  'Created as SKU 118842.',                                    '2026-08-15T13:05:00.000Z'],
    ['npr-12', '482924', 'Colgate Total Whitening Toothpaste 4.8oz Twin Pack', 'Extend Existing Variety', 'colgate.com/total/whitening',            'luis.ortiz@abconsultingpr.com',   'Created',  'Created as SKU 118843, linked to the existing variety.',    '2026-08-15T09:47:00.000Z'],
  ];

  return rows.map(([id, spot, desc, level, ref, email, status, notes, created]) => ({
    ...base,
    id: `sandbox-${id}`,
    idempotency_key: `sandbox-seed-${id}`,
    spot_number_link: spotLink(spot),
    suggested_description: desc,
    reference_link: ref,
    level,
    user_email: email,
    resolution_status: status,
    resolution_notes: notes,
    created_at: created,
    updated_at: created,
  }));
}

let sandboxIssues = [...buildSeedIssues(), ...buildSeedNewProductRequests()];
const issuesSubscribers = new Set();

function notifyIssues() {
  for (const fn of issuesSubscribers) {
    try { fn(sandboxIssues); } catch (e) { console.error('[sandbox] issues subscriber error:', e); }
  }
}

// ---------------------------------------------------------------------------
// Spot products (GET /spots/{id})
// ---------------------------------------------------------------------------

// The New Products Requests seed rows above carry invented spot links
// (482913-482924). Those ids are arbitrary — they are not guaranteed to mean
// anything in the real sw1dbtables_sw1spot table, so letting the Variety Type
// lookup fall through to the live API would either 401 (local dev is rarely on
// an auth-bypass origin) or answer with whatever unrelated spot happens to own
// that id in prod. Both are worse than a seeded answer.
//
// This is the one read path sandbox does intercept, and only because sandbox
// itself invented the ids being looked up.
//
// 482915 is deliberately absent so the "This spot is blank" state is
// reachable, and 482916 is present but empty so the other blank path — a spot
// that exists with nothing entered on it — is reachable too.
const SANDBOX_SPOT_PRODUCTS = {
  482913: [
    { upc: '000000000001', brand: 'Goya', description: 'Goya Coconut Water 33.8oz Tetra Pak', category: 'Beverages', size: '33.80', form: 'Tetra Pak', uom: 'Oz' },
    { upc: '000000000002', brand: 'Goya', description: 'Goya Coconut Water with Pulp 33.8oz', category: 'Beverages', size: '33.80', form: 'Tetra Pak', uom: 'Oz' },
  ],
  482914: [
    { upc: '000000000003', brand: 'Bounty', description: 'Bounty Select-A-Size Paper Towels 8 Double Rolls', category: 'Paper Towels', size: '8.00', form: 'Rollos', uom: 'Ct' },
  ],
  482916: [],
  482917: [
    { upc: '000000000004', brand: 'Tide', description: 'Tide Pods Spring Meadow 42 Count Tub', category: 'Laundry Detergent', size: '42.00', form: 'Tub', uom: 'Ct' },
    { upc: '000000000005', brand: 'Tide', description: 'Tide Pods Free & Gentle 42 Count Tub', category: 'Laundry Detergent', size: '42.00', form: 'Tub', uom: 'Ct' },
    { upc: '000000000006', brand: 'Downy', description: 'Downy Unstopables In-Wash Scent Booster 14oz', category: 'Laundry Additives', size: '14.00', form: 'Botella', uom: 'Oz' },
  ],
};

// Mirrors the GET /spots response shape so dataService.fetchSpotProducts can
// return it unchanged: { spots: [...], total_products }. An id with no seeded
// entry comes back as an empty spots array, which is exactly what the live
// batch endpoint answers for an unknown spot.
export function getSandboxSpotProducts(spotIds) {
  const ids = (Array.isArray(spotIds) ? spotIds : [spotIds])
    .map((id) => parseInt(id, 10))
    .filter((id) => !isNaN(id));

  const spots = ids
    .filter((id) => id in SANDBOX_SPOT_PRODUCTS)
    .map((id) => ({
      id,
      customername: SANDBOX_RETAILER,
      startdate: null,
      pagenumber: null,
      promotype: 'Regular',
      adtype: 'Regular',
      products: SANDBOX_SPOT_PRODUCTS[id].map((product) => ({
        ...product,
        regularprice: null,
        specialprice: null,
        prodqtyreg: null,
        prodqtyspec: null,
      })),
    }));

  return {
    spots,
    total_products: spots.reduce((sum, spot) => sum + spot.products.length, 0),
  };
}

export function getSandboxIssuesRaw() {
  return sandboxIssues;
}

export function subscribeSandboxIssues(fn) {
  issuesSubscribers.add(fn);
  fn(sandboxIssues);
  return () => issuesSubscribers.delete(fn);
}

export function resetSandboxIssues() {
  sandboxIssues = [...buildSeedIssues(), ...buildSeedNewProductRequests()];
  notifyIssues();
}

// Append rows coming from a QC Tool bulkInsertIssues call so the QC → IR
// round-trip is exercisable. Rows match the shape pushed by QCTool.jsx.
// ON CONFLICT (idempotency_key) DO NOTHING is mirrored here in JS.
export function addSandboxIssues(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return { inserted: [], skipped: 0 };
  const existingKeys = new Set(sandboxIssues.map(r => r.idempotency_key).filter(Boolean));
  const inserted = [];
  let skipped = 0;
  const created = nowIso();
  let nextSeq = sandboxIssues.length + 1;
  for (const raw of rows) {
    if (raw.idempotency_key && existingKeys.has(raw.idempotency_key)) {
      skipped++;
      continue;
    }
    const row = {
      ...raw,
      id: raw.id || `sandbox-runtime-${nextSeq++}`,
      created_at: raw.created_at || created,
      updated_at: raw.updated_at || created,
      // Match Lambda default — qc_status defaults to 'Pass' when not provided.
      qc_status: raw.qc_status || 'Pass',
    };
    if (row.idempotency_key) existingKeys.add(row.idempotency_key);
    inserted.push(row);
  }
  sandboxIssues = [...inserted, ...sandboxIssues];
  notifyIssues();
  return { inserted, skipped };
}

// Mutate a single sandbox row by id. Used by the updateIssue intercept so the
// IR UI reflects state changes (Resolved/Escalated etc.) instead of bouncing
// back to Pending on the next refresh.
export function updateSandboxIssue(id, fields) {
  let matched = null;
  sandboxIssues = sandboxIssues.map(row => {
    if (row.id !== id) return row;
    matched = { ...row, ...fields, updated_at: nowIso() };
    return matched;
  });
  if (matched) notifyIssues();
  return matched;
}

// Apply the same filter shape the Lambda applies to the in-memory store.
function filterRows({ sources, qcStatus, retailer, date }) {
  return sandboxIssues.filter(r => {
    if (sources && sources.length && !sources.includes(r.source)) return false;
    if (qcStatus && r.qc_status !== qcStatus) return false;
    if (retailer && r.retailer !== retailer) return false;
    if (date && r.date !== date) return false;
    return true;
  });
}

// Mirror of the Lambda summary query (lambda/routes/issues.js:64-77).
// GROUP BY retailer, date with the four COUNT FILTER aggregates.
export function getSandboxIssuesSummary({ sources, qcStatus } = {}) {
  const filtered = filterRows({ sources, qcStatus });
  const groups = new Map();
  for (const r of filtered) {
    const key = `${r.retailer}|${r.date}`;
    if (!groups.has(key)) {
      groups.set(key, {
        retailer: r.retailer,
        date: r.date,
        total_issues: 0,
        error_count: 0,
        pass_count: 0,
        resolved_error_count: 0,
        page_error_count: 0,
        spot_error_count: 0,
        product_error_count: 0,
        latest_created_at: null,
        _pages: new Set(),
      });
    }
    const g = groups.get(key);
    g.total_issues++;
    if (r.qc_status === 'Fail') g.error_count++;
    if (r.qc_status === 'Pass') g.pass_count++;
    if (r.qc_status === 'Fail' && r.resolution_status === 'Resolved') g.resolved_error_count++;
    if (r.qc_status === 'Fail') {
      if (r.level === 'Page') g.page_error_count++;
      else if (r.level === 'Spot') g.spot_error_count++;
      else if (typeof r.level === 'string' && r.level.startsWith('Product/UPC') && !r.level.includes('Context')) {
        g.product_error_count++;
      }
    }
    if (r.page_number != null) g._pages.add(r.page_number);
    if (r.created_at && (!g.latest_created_at || r.created_at > g.latest_created_at)) {
      g.latest_created_at = r.created_at;
    }
  }
  return Array.from(groups.values())
    .map(g => ({
      retailer: g.retailer,
      date: g.date,
      total_issues: g.total_issues,
      error_count: g.error_count,
      pass_count: g.pass_count,
      resolved_error_count: g.resolved_error_count,
      page_error_count: g.page_error_count,
      spot_error_count: g.spot_error_count,
      product_error_count: g.product_error_count,
      latest_created_at: g.latest_created_at,
      page_count: g._pages.size,
    }))
    .sort((a, b) => {
      // Most recent activity first (matches lambda summary ORDER BY MAX(created_at) DESC).
      const aa = a.latest_created_at || '';
      const bb = b.latest_created_at || '';
      if (aa !== bb) return aa < bb ? 1 : -1;
      return a.retailer.localeCompare(b.retailer);
    });
}

export function getSandboxIssuesFiltered(filter) {
  return filterRows(filter);
}
